'use client';

/**
 * TRUS-778: a navigator approves Wren's matches before any provider is
 * contacted. Shown on the referral's Provider Matching card only while the
 * member navigation agent is waiting on a round (GET /api/member-nav/requests/:id
 * returns awaiting_approval). Hidden for every other referral, and for staff
 * outside the hub org (the endpoint answers 403/404).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/services/apiClient';

interface WrenMatch {
  id: number;
  provider_type: string;
  name?: string | null;
  email?: string | null;
  fax?: string | null;
  phone?: string | null;
  address?: string | null;
}

interface WrenRequest {
  state: string;
  round: number;
  urgent: boolean;
  awaiting_approval: boolean;
  matches: WrenMatch[];
}

interface WrenApprovalBannerProps {
  referralId: number;
  onChanged?: () => void;
}

export function WrenApprovalBanner({ referralId, onChanged }: WrenApprovalBannerProps) {
  const { t } = useAppTranslation();
  const [req, setReq] = useState<WrenRequest | null>(null);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<'approve' | 'skip' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<WrenRequest | null>(`/api/member-nav/requests/${referralId}`);
      const data = res.data;
      setReq(data && data.awaiting_approval ? data : null);
      setChosen(new Set((data?.matches || []).map((m) => m.id)));
    } catch {
      setReq(null); // not a Wren request, or not this user's hub
    }
  }, [referralId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!req) return null;

  const toggle = (id: number) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const act = async (kind: 'approve' | 'skip') => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'approve') {
        await apiClient.post(`/api/member-nav/requests/${referralId}/approve`, {
          match_ids: Array.from(chosen),
        });
      } else {
        await apiClient.post(`/api/member-nav/requests/${referralId}/skip`);
      }
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          t('serviceReferrals.wren.actionFailed', 'That did not go through. Please try again.'),
      );
    } finally {
      setBusy(null);
    }
  };

  const channel = (m: WrenMatch) => {
    if (m.provider_type === 'cbo') return t('serviceReferrals.wren.viaInbox', 'WeaveIn inbox');
    if (m.email && m.fax) return t('serviceReferrals.wren.viaEmailFax', 'Email and fax');
    if (m.email) return t('serviceReferrals.wren.viaEmail', 'Email');
    if (m.fax) return t('serviceReferrals.wren.viaFax', 'Fax');
    return t('serviceReferrals.wren.viaManual', 'No email or fax: contact by hand');
  };

  return (
    <Alert className="border-v2accent bg-v2accent-soft">
      <AlertDescription>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-v2accent-ink" />
            <span className="font-medium text-ink">
              {t('serviceReferrals.wren.title', 'Wren is waiting for your approval')}
            </span>
            {req.urgent && <Badge variant="danger">{t('serviceReferrals.urgent', 'Urgent')}</Badge>}
            <Badge variant="muted">{t('serviceReferrals.wren.round', 'Round {{n}}', { n: req.round })}</Badge>
          </div>
          <p className="text-sm text-ink-500">
            {t(
              'serviceReferrals.wren.body',
              'No one has been contacted yet. Choose the partners Wren may send this request to. The first to accept takes the member.',
            )}
          </p>
          <ul className="space-y-2">
            {req.matches.map((m) => (
              <li key={m.id}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={chosen.has(m.id)}
                    onChange={() => toggle(m.id)}
                    disabled={busy !== null}
                  />
                  <span>
                    <span className="font-medium text-ink">
                      {m.name || t('serviceReferrals.provider', 'Provider')}
                    </span>
                    <span className="text-ink-500">
                      {' · '}
                      {m.provider_type === 'cbo'
                        ? t('serviceReferrals.wren.platformOrg', 'Platform org')
                        : t('serviceReferrals.wren.directory', 'Directory')}
                      {' · '}
                      {channel(m)}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {error && <p className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => act('approve')} disabled={busy !== null || chosen.size === 0}>
              {busy === 'approve' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('serviceReferrals.wren.approve', 'Approve and send to {{n}}', { n: chosen.size })}
            </Button>
            <Button size="sm" variant="outline" onClick={() => act('skip')} disabled={busy !== null}>
              {busy === 'skip' && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('serviceReferrals.wren.skip', 'Skip these and find others')}
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
